import { getRailsClient } from "../client";

const levelsApiBase = "/api/internal_api/v1/levels";

const allLevelsApiRoute = "/api/internal_api/atlas/v1/current_user/levels";

// The "role" (active path) nested inside each course or quest from /levels
interface LevelCourseRole {
  id: number;
  name: string;
  description: string;
  totalPoints: number;
  thumbImageUrl: string | null;
  activated: boolean;
  archived: boolean;
  levelId: number;
  active: number;
  completed: number;
  lessonsCount: number;
  isSjContent: boolean;
  mode: string;
  updatedAt: string;
  roleGroupId: number;
  type: string | null;
  version: string;
  publicId: string;
  roleGroupPublicId: string;
  restricted: boolean;
  reassign: boolean;
  audiences: Array<{ id: number; name: string; value: string; developer: boolean }>;
  recommended: boolean;
  includeCustomCertDescription: boolean;
}

// A lesson item nested inside a course or quest from /levels
interface CourseLessonItem {
  id: number;
  name: string;
  description: string;
  duration: string;
  durationNumber: string;
  levelId: number;
  result: string;
  assessmentPassed: boolean | null;
  freeAccess: boolean;
  userCanAfford: boolean;
  required: boolean;
  lessonGroupId: number;
  inMyList: boolean;
  thumbnailUrl: string;
  embedUrl: string;
  embedCode: string;
  lessonKey: string;
}

interface LevelBase<TRankName extends string | null> {
  id: number;
  name: string;
  description: string;
  rankName: TRankName;
  image: string | null;
  hasDojoActivities: boolean;
  userStats: { current: number; total: number; progress: number };
  hasActiveRoles: boolean;
  price: string;
  visibleToUser: boolean;
  status: "locked" | "in_progress" | "passed";
  role: LevelCourseRole | null;
  lessons: CourseLessonItem[];
  totalRoles: number;
}

export type Level = LevelBase<string>;

// Quests share the same shape as Level but rankName can be null
export type Quest = LevelBase<string | null>;

interface LevelsResponse {
  courses: Level[];
  quests: Quest[];
  lessons: Record<string, CourseLessonItem[]>;
  currentLevel: {
    id: number;
    name: string;
    rankName: string;
    image: string | null;
    statsCurrent: number;
    statsTotal: number;
    statsProgress: number;
    activeRolesNumber: number;
    userRole: LevelCourseRole | null;
  } | null;
  passedEnrollment: {
    id: number;
    completedAtDate: string;
    completionShowModal: boolean;
    levelId: number;
    levelName: string;
    levelRankName: string;
    levelDescription: string;
    levelHasDojoActivities: boolean;
    passedLessonsCount: number;
    totalLessonsCount: number;
    role: LevelCourseRole | null;
  } | null;
}

// Response from GET /api/internal_api/atlas/v1/current_user/levels.
// activePath/paths match the context's ActivePath and LevelPath interfaces structurally.
export interface AllLevelDataResponse {
  courses?: Array<{
    id: number;
    name: string;
    description: string;
    status: "locked" | "in_progress" | "passed";
    rankName: string;
    hasActivePaths?: boolean;
    visibleToUser?: boolean;
    activePath?: {
      id: number;
      roleId: number;
      name: string;
      description: string;
      collectionKey: string | null;
      roleEnrollmentId: number;
      lessonsInEnrollment: number;
      lessonsPassed: number;
      estimatedTimeToComplete: number;
      completed: boolean;
      totalPoints: number;
      nextLesson: {
        id: number;
        lessonKey: string;
        name: string;
        description: string;
        estimatedTime: number;
        difficulty: string;
        lessonType: string;
        status: string;
        required: boolean;
      } | null;
      enrollmentType: string;
      levelRank: string | null;
    } | null;
    paths?: Array<{
      id: number;
      name: string;
      description: string;
      collectionKey: string | null;
      status: "locked" | "in_progress" | "passed" | "abandoned";
    }>;
  }>;
  quests?: Array<{
    id: number;
    name: string;
    description: string;
    status: "locked" | "in_progress" | "passed";
    rankName: string | null;
    hasActivePaths?: boolean;
    visibleToUser?: boolean;
    activePath: null;
    paths?: Array<{
      id: number;
      name: string;
      description: string;
      collectionKey: string | null;
      status: "locked" | "in_progress" | "passed" | "abandoned";
    }>;
  }>;
}

export const getLevels = async () => {
  try {
    const { data } = await getRailsClient({ useToast: true }).get<LevelsResponse>(levelsApiBase);
    return data;
  } catch (error) {
    console.error("Error loading levels:", error);
    throw error;
  }
};

export const getAllLevelData = async () => {
  const response = await getRailsClient({ useToast: true }).get<AllLevelDataResponse>(
    allLevelsApiRoute
  );

  if (response.status === 200) {
    return response.data;
  } else {
    throw new Error(`getAllLevelData: Unable to get level data, status code ${response.status}`);
  }
};
